import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button, Form, Input, InputNumber, message, Select } from 'antd';
import { useMutation, useQuery } from '@apollo/client';
import { CREATE_BAG, CreateBagInput, CreateBagResponse } from '../graphql/mutations/createBag';
import { TTBag } from '../graphql/mutations/createTTBag';
import { useWarehouseContext } from '../../../../contexts/WarehouseContext';
import { useTranslation } from 'react-i18next';
import { GET_ALL_CARRIER_OPTIONS, GetAllCarrierOptionsResponse } from '../graphql/queries/getAllCarrierOptions';
import {
    GET_WAREHOUSE_LIST_BY_HUB_ID,
    GetWarehouseListByHubIdResponse,
} from '../graphql/queries/getWarehouseListByHubId';
import {
    GET_AREAS_BY_WAREHOUSE_ID,
    GetAreasByWarehouseIdResponse,
} from '../../../../graphql/queries/getAreasByWarehouseId';
import { GET_HUB_LIST, GetHubListResponse } from '../graphql/queries/getHubList';

// ---- 段码工具函数 ----

const extractFirstTwoSegments = (sortingCode: string): string => {
    if (!sortingCode) return '';
    const parts = sortingCode.split('-');
    if (parts.length >= 2) return parts.slice(0, 2).join('-');
    return sortingCode;
};

const extractThirdSegmentLetter = (sortingCode: string): string => {
    if (!sortingCode) return '';
    const parts = sortingCode.split('-');
    if (parts.length >= 3) {
        return parts[2].match(/^[A-Za-z]+/)?.[0] || '';
    }
    return '';
};

const extractThirdSegmentDigit = (sortingCode: string): string => {
    if (!sortingCode) return '';
    const parts = sortingCode.split('-');
    if (parts.length >= 3) {
        return parts[2].replace(/^[A-Za-z]+/, '');
    }
    return '';
};

const convertThreeSegmentToFourSegment = (threeSegmentCode: string): string => {
    if (!threeSegmentCode) return threeSegmentCode;
    const parts = threeSegmentCode.split('-');
    if (parts.length !== 3) return threeSegmentCode;
    const thirdSegment = parts[2];
    const letterPart = thirdSegment.match(/^[A-Za-z]+/)?.[0] || '';
    const digitPart = thirdSegment.replace(/^[A-Za-z]+/, '');
    if (digitPart) {
        return `${parts[0]}-${parts[1]}-${letterPart}-${digitPart}`;
    }
    return threeSegmentCode;
};

const generateBagSortingCode = (
    bagType: number,
    hubCode?: string,
    warehouseCode?: string,
    areaCode?: string
): string => {
    if (bagType === 1) return hubCode || '';
    if (bagType === 2) return warehouseCode || '';
    if (bagType === 3) {
        if (!areaCode) return '';
        const firstTwo = extractFirstTwoSegments(areaCode);
        const thirdLetter = extractThirdSegmentLetter(areaCode);
        if (firstTwo && thirdLetter) {
            return `${firstTwo}-${thirdLetter}`;
        }
        return areaCode;
    }
    if (bagType === 4) {
        if (!areaCode) return '';
        const firstTwo = extractFirstTwoSegments(areaCode);
        const thirdLetter = extractThirdSegmentLetter(areaCode);
        const thirdDigit = extractThirdSegmentDigit(areaCode);
        if (firstTwo && thirdLetter && thirdDigit) {
            return `${firstTwo}-${thirdLetter}-${thirdDigit}`;
        }
        return convertThreeSegmentToFourSegment(areaCode);
    }
    return '';
};

export interface NewBagFormProps {
    visible: boolean;
    onClose: () => void;
    onSubmitSuccess: (bags: TTBag[], printCopies: number) => void;
}

const NewBagForm: React.FC<NewBagFormProps> = ({ visible, onClose, onSubmitSuccess }) => {
    const { currentWarehouse } = useWarehouseContext();
    const [form] = Form.useForm();
    const { t } = useTranslation();
    const submittingRef = useRef(false);

    const [selectedBagType, setSelectedBagType] = useState<number>(1);
    const [selectedStage1, setSelectedStage1] = useState<string>('');
    const [selectedStage2, setSelectedStage2] = useState<string>('');
    const [selectedStage3, setSelectedStage3] = useState<string>('');
    const [selectedStage4, setSelectedStage4] = useState<string>('');
    const [destination, setDestination] = useState<string>('');
    const [sortingCode, setSortingCode] = useState<string>('');
    const [isAllAreasSelected, setIsAllAreasSelected] = useState<boolean>(false);
    const [allAreasSortingCodes, setAllAreasSortingCodes] = useState<string[]>([]);

    const { data: hubData } = useQuery<GetHubListResponse>(GET_HUB_LIST, { skip: !visible });
    const { data: warehouseData } = useQuery<GetWarehouseListByHubIdResponse>(
        GET_WAREHOUSE_LIST_BY_HUB_ID,
        { variables: { hubId: selectedStage1 }, skip: !selectedStage1 || !visible }
    );
    const { data: areaData } = useQuery<GetAreasByWarehouseIdResponse>(
        GET_AREAS_BY_WAREHOUSE_ID,
        { variables: { warehouseId: selectedStage2 }, skip: !selectedStage2 || !visible }
    );

    useEffect(() => {
        if (selectedBagType === 1 && selectedStage1 && hubData?.hubList) {
            const selectedHub = hubData.hubList.find(hub => hub.id === selectedStage1);
            const hubName = selectedHub?.name || '';
            const hubSortingCode = selectedHub?.sortingCode || '';
            const bagSortingCode = generateBagSortingCode(1, hubSortingCode);
            setSortingCode(bagSortingCode);
            setDestination(hubName);
            form.setFieldsValue({ destination: hubName, sortingCode: bagSortingCode });
        }
    }, [selectedBagType, selectedStage1, hubData?.hubList, form]);

    useEffect(() => {
        if (selectedBagType === 2 && selectedStage1 && selectedStage2 && warehouseData?.warehouseList && hubData?.hubList) {
            const selectedHub = hubData.hubList.find(hub => hub.id === selectedStage1);
            const selectedWarehouse = warehouseData.warehouseList.find(wh => wh.id === selectedStage2);
            const hubSortingCode = selectedHub?.sortingCode || '';
            const warehouseSortingCode = selectedWarehouse?.sortingCode || '';
            const warehouseName = selectedWarehouse?.name || '';
            const bagSortingCode = generateBagSortingCode(2, hubSortingCode, warehouseSortingCode);
            setSortingCode(bagSortingCode);
            setDestination(warehouseName);
            form.setFieldsValue({ destination: warehouseName, sortingCode: bagSortingCode });
        }
    }, [selectedBagType, selectedStage1, selectedStage2, warehouseData, hubData?.hubList, form]);

    const { data: carrierData, loading: carrierLoading } = useQuery<GetAllCarrierOptionsResponse>(GET_ALL_CARRIER_OPTIONS, {
        skip: !visible,
    });
    const swiftXCarrierOptions = useMemo(
        () => carrierData?.getAllCarrierOptions.filter(c => c.value !== -1).map(c => ({ value: c.value, label: c.name })) || [],
        [carrierData]
    );

    const [createBag, { loading: creating }] = useMutation<CreateBagResponse>(CREATE_BAG);

    const resetSwiftXState = () => {
        setSelectedBagType(1);
        setSelectedStage1('');
        setSelectedStage2('');
        setSelectedStage3('');
        setSelectedStage4('');
        setDestination('');
        setSortingCode('');
        setIsAllAreasSelected(false);
        setAllAreasSortingCodes([]);
    };

    useEffect(() => {
        if (visible) {
            form.resetFields();
            resetSwiftXState();
        }
    }, [visible, form]);

    useEffect(() => {
        if (visible && swiftXCarrierOptions.length > 0) {
            setTimeout(() => {
                form.setFieldsValue({ carrierCode: swiftXCarrierOptions[0].value });
            }, 0);
        }
    }, [visible, swiftXCarrierOptions, form]);

    const handleBagTypeChange = (value: number) => {
        setSelectedBagType(value);
        setSelectedStage1('');
        setSelectedStage2('');
        setSelectedStage3('');
        setSelectedStage4('');
        setDestination('');
        setSortingCode('');
        setIsAllAreasSelected(false);
        setAllAreasSortingCodes([]);
        form.setFieldsValue({ stage1: undefined, stage2: undefined, stage3: undefined, stage4: undefined, destination: '', sortingCode: '' });
    };

    const handleStage1Change = (value: string) => {
        setSelectedStage1(value);
        setSelectedStage2('');
        setSelectedStage3('');
        setSelectedStage4('');
        setDestination('');
        setSortingCode('');
        setIsAllAreasSelected(false);
        setAllAreasSortingCodes([]);
        form.setFieldsValue({ stage2: undefined, stage3: undefined, stage4: undefined, destination: '', sortingCode: '' });
    };

    const handleStage2Change = (value: string) => {
        setSelectedStage2(value);
        setSelectedStage3('');
        setSelectedStage4('');
        setDestination('');
        setSortingCode('');
        setIsAllAreasSelected(false);
        setAllAreasSortingCodes([]);
        form.setFieldsValue({ stage3: undefined, stage4: undefined, destination: '', sortingCode: '' });
    };

    /** 生成批量模式的 sortingCode 展示文本 */
    const buildBatchSortingCodeDisplay = (areaSortingCodes: string[], warehouseSortingCode: string): string => {
        const selectedHub = hubData?.hubList?.find(hub => hub.id === selectedStage1);
        const hubSortingCode = selectedHub?.sortingCode || '';
        if (areaSortingCodes.length === 1) {
            return generateBagSortingCode(selectedBagType, hubSortingCode, warehouseSortingCode, areaSortingCodes[0]);
        } else if (areaSortingCodes.length === 2) {
            return areaSortingCodes.map(code =>
                generateBagSortingCode(selectedBagType, hubSortingCode, warehouseSortingCode, code)
            ).join(', ');
        } else if (areaSortingCodes.length > 2) {
            const firstCode = generateBagSortingCode(selectedBagType, hubSortingCode, warehouseSortingCode, areaSortingCodes[0]);
            const lastCode = generateBagSortingCode(selectedBagType, hubSortingCode, warehouseSortingCode, areaSortingCodes[areaSortingCodes.length - 1]);
            return `${firstCode}...${lastCode}(${areaSortingCodes.length})`;
        }
        return warehouseSortingCode;
    };

    const handleStage3Change = (value: string) => {
        setSelectedStage3(value);

        if ((selectedBagType === 3 || selectedBagType === 4) && selectedStage1 && selectedStage2) {
            if (value === 'all') {
                setIsAllAreasSelected(true);
                const areas = areaData?.areaList || [];
                const areaSortingCodes = areas.map(area => area.sortingCode).filter(code => code) as string[];
                setAllAreasSortingCodes(areaSortingCodes);

                const selectedWarehouse = warehouseData?.warehouseList.find(wh => wh.id === selectedStage2);
                const warehouseSortingCode = selectedWarehouse?.sortingCode || '';
                const warehouseName = selectedWarehouse?.name || '';
                const display = buildBatchSortingCodeDisplay(areaSortingCodes, warehouseSortingCode);

                setSortingCode(display);
                setDestination(warehouseName);

                if (selectedBagType === 4) {
                    setSelectedStage4('all');
                    form.setFieldsValue({ stage4: 'all' });
                }
                form.setFieldsValue({ destination: warehouseName, sortingCode: display });
            } else {
                setIsAllAreasSelected(false);
                setAllAreasSortingCodes([]);

                const selectedWarehouse = warehouseData?.warehouseList.find(wh => wh.id === selectedStage2);
                const warehouseSortingCode = selectedWarehouse?.sortingCode || '';
                const warehouseName = selectedWarehouse?.name || '';

                if (selectedBagType === 4) {
                    setSelectedStage4('');
                    form.setFieldsValue({ stage4: undefined });
                }

                if (selectedBagType === 3) {
                    const firstTwo = extractFirstTwoSegments(warehouseSortingCode || '');
                    const bagSortingCode = firstTwo ? `${firstTwo}-${value}` : warehouseSortingCode;
                    setDestination(warehouseName);
                    setSortingCode(bagSortingCode);
                    form.setFieldsValue({ destination: warehouseName, sortingCode: bagSortingCode });
                } else if (selectedBagType === 4) {
                    setDestination(warehouseName);
                    setSortingCode('');
                    form.setFieldsValue({ destination: warehouseName, sortingCode: '' });
                }
            }
        }
    };

    const handleStage4Change = (value: string) => {
        setSelectedStage4(value);

        if (selectedBagType === 4 && selectedStage1 && selectedStage2 && warehouseData?.warehouseList && hubData?.hubList) {
            const selectedWarehouse = warehouseData.warehouseList.find(wh => wh.id === selectedStage2);
            const warehouseSortingCode = selectedWarehouse?.sortingCode || '';
            const warehouseName = selectedWarehouse?.name || '';

            if (value === 'all') {
                setIsAllAreasSelected(true);
                const areas = areaData?.areaList || [];
                const matchingAreas = areas.filter(area => {
                    if (selectedStage3 && selectedStage3 !== 'all') {
                        const letter = extractThirdSegmentLetter(area.sortingCode || '');
                        return letter === selectedStage3;
                    }
                    return true;
                });
                const areaSortingCodes = matchingAreas.map(area => area.sortingCode).filter(code => code) as string[];
                setAllAreasSortingCodes(areaSortingCodes);
                const display = buildBatchSortingCodeDisplay(areaSortingCodes, warehouseSortingCode);
                setDestination(warehouseName);
                setSortingCode(display);
                form.setFieldsValue({ destination: warehouseName, sortingCode: display });
            } else {
                setIsAllAreasSelected(false);
                setAllAreasSortingCodes([]);
                if (selectedStage3 && selectedStage3 !== 'all') {
                    const firstTwo = extractFirstTwoSegments(warehouseSortingCode || '');
                    const bagSortingCode = firstTwo ? `${firstTwo}-${selectedStage3}-${value}` : warehouseSortingCode;
                    setDestination(warehouseName);
                    setSortingCode(bagSortingCode);
                    form.setFieldsValue({ destination: warehouseName, sortingCode: bagSortingCode });
                }
            }
        }
    };

    const handleSubmit = async () => {
        if (submittingRef.current) return;
        submittingRef.current = true;
        try {
            const values = await form.validateFields();

            // 验证四段模式必须选择第四段
            if (selectedBagType === 4 && !selectedStage4) {
                message.error(t('wms.bags.messages.pleaseSelectStage4') || 'Please select a number');
                submittingRef.current = false;
                return;
            }

            let input: CreateBagInput;
            const isBatchMode = (selectedBagType === 3 && isAllAreasSelected) ||
                                (selectedBagType === 4 && (selectedStage3 === 'all' || selectedStage4 === 'all'));

            if (isBatchMode) {
                input = {
                    bagType: selectedBagType,
                    sortingCode: '',
                    warehouseId: currentWarehouse?.id!,
                    copyNum: values.bagQuantity || 1,
                    batchMode: true,
                    warehouseIdForBatch: selectedStage2,
                    carrierCode: values.carrierCode,
                    stage3Letter: selectedStage3 === 'all' ? 'all' : selectedStage3,
                    stage4Digit: selectedStage4 === 'all' ? 'all' : selectedStage4,
                };
            } else {
                input = {
                    bagType: selectedBagType,
                    sortingCode: sortingCode,
                    warehouseId: currentWarehouse?.id!,
                    copyNum: values.bagQuantity || 1,
                    batchMode: false,
                    carrierCode: values.carrierCode,
                };
            }

            const result = await createBag({ variables: { input } });
            if (result.data?.result.errorMessage) {
                message.error(result.data.result.errorMessage);
                return;
            }
            if (result.data?.result.bags) {
                const totalBags = result.data.result.successCount;
                const successMessage = isBatchMode
                    ? t('wms.bags.messages.successfullyCreatedTagsForAreas', { totalBags, areasLength: allAreasSortingCodes.length })
                    : t('wms.bags.messages.successfullyCreatedTags', { count: totalBags });
                message.success(successMessage);
                const bags: TTBag[] = result.data.result.bags.map((b: any) => ({
                    id: b.id?.toString() || '',
                    bagNumber: b.bagNumber || '',
                    bagType: b.bagType,
                    businessType: b.businessType,
                    warehouseId: b.warehouseId,
                    sortingCode: b.sortingCode,
                    status: b.status,
                    createdAt: b.createdAt,
                }));
                onSubmitSuccess(bags, values.printCopies || 1);
                onClose();
            }
        } catch (error) {
            console.error('Form submission failed:', error);
            message.error(t('wms.bags.messages.failedToCreateTags'));
        } finally {
            submittingRef.current = false;
        }
    };

    return (
        <Form
            form={form}
            layout="horizontal"
            labelCol={{ span: 7 }}
            wrapperCol={{ span: 17 }}
            initialValues={{ bagQuantity: 1, printCopies: 1, bagType: 1 }}
        >
            <Form.Item
                name="bagType"
                label={t('wms.bags.pageDetails.tagType')}
                rules={[{ required: true, message: t('wms.bags.messages.pleaseSelectBagType') || '' }]}
            >
                <Select
                    value={selectedBagType}
                    onChange={handleBagTypeChange}
                    options={[
                        { value: 1, label: '1 stage' },
                        { value: 2, label: '2 stages' },
                        { value: 3, label: '3 stages' },
                        { value: 4, label: '4 stages' },
                    ]}
                />
            </Form.Item>

            <Form.Item
                name="stage1"
                label={t('wms.bags.pageDetails.stage1')}
                rules={[{ required: true, message: t('wms.bags.messages.pleaseSelectStage1') || '' }]}
            >
                <Select
                    placeholder={t('wms.bags.messages.pleaseSelectStage1')}
                    onChange={handleStage1Change}
                    options={hubData?.hubList?.map(hub => ({ value: hub.id, label: hub.name }))}
                />
            </Form.Item>

            {(selectedBagType === 2 || selectedBagType === 3 || selectedBagType === 4) && (
                <Form.Item
                    name="stage2"
                    label={t('wms.bags.pageDetails.stage2')}
                    rules={[{ required: true, message: t('wms.bags.messages.pleaseSelectStage2') || '' }]}
                >
                    <Select
                        placeholder={t('wms.bags.messages.pleaseSelectStage2')}
                        onChange={handleStage2Change}
                        options={warehouseData?.warehouseList.map(wh => ({ value: wh.id, label: wh.name }))}
                    />
                </Form.Item>
            )}

            {(selectedBagType === 3 || selectedBagType === 4) && (
                <Form.Item
                    name="stage3"
                    label={t('wms.bags.pageDetails.stage3') || 'Route'}
                    rules={[{ required: true, message: t('wms.bags.messages.pleaseSelectStage3') || '' }]}
                >
                    <Select
                        placeholder={t('wms.bags.messages.pleaseSelectStage3')}
                        onChange={handleStage3Change}
                        disabled={!selectedStage2}
                        options={[
                            { value: 'all', label: t('wms.bags.options.allRegions') || 'All Regions' },
                            ...Array.from(new Set(
                                areaData?.areaList
                                    ?.map(area => extractThirdSegmentLetter(area.sortingCode || ''))
                                    .filter(letter => letter) || []
                            )).map(letter => ({
                                value: letter,
                                label: letter
                            }))
                        ]}
                    />
                </Form.Item>
            )}

            {selectedBagType === 4 && (
                <Form.Item
                    name="stage4"
                    label={t('wms.bags.pageDetails.stage4') || 'Number'}
                    rules={[{ required: true, message: t('wms.bags.messages.pleaseSelectStage4') || '' }]}
                >
                    <Select
                        placeholder={t('wms.bags.messages.pleaseSelectStage4')}
                        onChange={handleStage4Change}
                        disabled={!selectedStage3 || !selectedStage2}
                        value={selectedStage4}
                        options={
                            selectedStage3 === 'all'
                                ? [{ value: 'all', label: t('wms.bags.options.allNumbers') || 'All Numbers' }]
                                : [
                                    { value: 'all', label: t('wms.bags.options.allNumbers') || 'All Numbers' },
                                    ...Array.from(new Set(
                                        areaData?.areaList
                                            ?.filter(area => {
                                                if (selectedStage3 && selectedStage3 !== 'all') {
                                                    const letter = extractThirdSegmentLetter(area.sortingCode || '');
                                                    return letter === selectedStage3;
                                                }
                                                return false;
                                            })
                                            .map(area => extractThirdSegmentDigit(area.sortingCode || ''))
                                            .filter(digit => digit) || []
                                    )).map(digit => ({
                                        value: digit,
                                        label: digit
                                    }))
                                ]
                        }
                    />
                </Form.Item>
            )}

            <Form.Item name="destination" label={t('wms.bags.pageDetails.destination')}>
                <Input value={destination} disabled />
            </Form.Item>

            <Form.Item name="sortingCode" label={t('wms.bags.pageDetails.sortingCode')}>
                <Input value={sortingCode} disabled />
            </Form.Item>

            <Form.Item
                name="bagQuantity"
                label={t('wms.bags.pageDetails.bagQuantity')}
                rules={[{ required: true, message: t('wms.bags.messages.pleaseEnterBagQuantity') || '' }]}
            >
                <InputNumber min={1} style={{ width: '100%' }} />
            </Form.Item>

            <Form.Item
                name="carrierCode"
                label={t('wms.bags.pageDetails.carrier')}
                rules={[{ required: true, message: t('wms.bags.messages.pleaseSelectCarrier') || '' }]}
            >
                <Select
                    placeholder={t('wms.bags.placeholders.carrier')}
                    options={swiftXCarrierOptions}
                    loading={carrierLoading}
                />
            </Form.Item>

            <Form.Item
                name="printCopies"
                label={t('wms.bags.pageDetails.printCopies')}
                rules={[{ required: true, message: t('wms.bags.messages.pleaseEnterPrintCopies') || '' }]}
            >
                <InputNumber min={1} style={{ width: '100%' }} />
            </Form.Item>

            <div className="flex justify-end gap-2">
                <Button onClick={onClose}>{t('components.button.cancel')}</Button>
                <Button type="primary" loading={creating} onClick={handleSubmit}>
                    {t('components.button.create')}
                </Button>
            </div>
        </Form>
    );
};

export default NewBagForm;

